/**
 * Procedure Bundles — "Patients also need..." smart suggestions.
 *
 * When a user adds a primary procedure, we surface related codes they
 * likely need (anesthesia, imaging, PT, follow-ups, etc.).
 * Keyed by the primary CPT/DRG code.
 */

export interface BundleSuggestion {
  code: string;
  codeType: string;
  description: string;
  phase: string;
  quantity: number;
  estimatedCost: number;
  setting?: 'inpatient' | 'outpatient' | 'unspecified';
  reason: string; // Why this is suggested
}

export interface ProcedureBundle {
  triggerCodes: string[]; // CPT codes that activate this bundle
  triggerKeywords: string[]; // keywords in description that also trigger
  label: string;
  suggestions: BundleSuggestion[];
}

export const PROCEDURE_BUNDLES: ProcedureBundle[] = [
  {
    triggerCodes: ['27447'],
    triggerKeywords: ['knee replacement', 'knee arthroplasty'],
    label: 'Knee Replacement',
    suggestions: [
      { code: '01402', codeType: 'CPT', description: 'Anesthesia for knee replacement', phase: 'Surgery', quantity: 1, estimatedCost: 2500, setting: 'inpatient', reason: 'Required for surgery' },
      { code: '0360', codeType: 'REV', description: 'Operating room', phase: 'Surgery', quantity: 1, estimatedCost: 10000, setting: 'inpatient', reason: 'Operating room fees' },
      { code: '0274', codeType: 'REV', description: 'Knee implant prosthesis', phase: 'Surgery', quantity: 1, estimatedCost: 6000, setting: 'inpatient', reason: 'Implant device' },
      { code: '97110', codeType: 'CPT', description: 'Physical therapy', phase: 'Physical Therapy', quantity: 24, estimatedCost: 4800, setting: 'outpatient', reason: '~24 PT sessions typical' },
      { code: '99213', codeType: 'CPT', description: 'Follow-up visits', phase: 'Follow-Up', quantity: 4, estimatedCost: 800, setting: 'outpatient', reason: 'Post-op monitoring' },
    ],
  },
  {
    triggerCodes: ['27130'],
    triggerKeywords: ['hip replacement', 'hip arthroplasty'],
    label: 'Hip Replacement',
    suggestions: [
      { code: '01214', codeType: 'CPT', description: 'Anesthesia for hip replacement', phase: 'Surgery', quantity: 1, estimatedCost: 2500, setting: 'inpatient', reason: 'Required for surgery' },
      { code: '0360', codeType: 'REV', description: 'Operating room', phase: 'Surgery', quantity: 1, estimatedCost: 10000, setting: 'inpatient', reason: 'Operating room fees' },
      { code: '0274', codeType: 'REV', description: 'Hip implant prosthesis', phase: 'Surgery', quantity: 1, estimatedCost: 7000, setting: 'inpatient', reason: 'Implant device' },
      { code: '97110', codeType: 'CPT', description: 'Physical therapy', phase: 'Rehabilitation', quantity: 24, estimatedCost: 4800, setting: 'outpatient', reason: '~24 PT sessions typical' },
      { code: '99213', codeType: 'CPT', description: 'Follow-up visits', phase: 'Follow-Up', quantity: 4, estimatedCost: 800, setting: 'outpatient', reason: 'Post-op monitoring' },
    ],
  },
  {
    triggerCodes: ['93458', '92928'],
    triggerKeywords: ['cardiac catheterization', 'stent placement', 'heart attack'],
    label: 'Cardiac Intervention',
    suggestions: [
      { code: '0450', codeType: 'REV', description: 'Emergency room', phase: 'Emergency Response', quantity: 1, estimatedCost: 5000, setting: 'inpatient', reason: 'ER evaluation' },
      { code: '93010', codeType: 'CPT', description: 'EKG', phase: 'Emergency Response', quantity: 3, estimatedCost: 150, setting: 'inpatient', reason: 'Cardiac monitoring' },
      { code: '0360', codeType: 'REV', description: 'Operating room / cath lab', phase: 'Surgery', quantity: 1, estimatedCost: 18000, setting: 'inpatient', reason: 'Cath lab fees' },
      { code: '0274', codeType: 'REV', description: 'Coronary stent device', phase: 'Surgery', quantity: 2, estimatedCost: 12000, setting: 'inpatient', reason: 'Stent device(s)' },
      { code: '93306', codeType: 'CPT', description: 'Echocardiogram', phase: 'Recovery', quantity: 2, estimatedCost: 2000, setting: 'inpatient', reason: 'Post-procedure imaging' },
      { code: '99214', codeType: 'CPT', description: 'Cardiology follow-up', phase: 'Follow-Up', quantity: 3, estimatedCost: 900, setting: 'outpatient', reason: 'Ongoing cardiac care' },
    ],
  },
  {
    triggerCodes: ['59400', '59510', '59425'],
    triggerKeywords: ['delivery', 'obstetric', 'c-section', 'cesarean', 'pregnancy'],
    label: 'Obstetric Care',
    suggestions: [
      { code: '76805', codeType: 'CPT', description: 'Pregnancy ultrasound', phase: 'Prenatal Care', quantity: 3, estimatedCost: 1200, setting: 'outpatient', reason: '~3 ultrasounds during pregnancy' },
      { code: '80055', codeType: 'CPT', description: 'Obstetric blood panel', phase: 'Prenatal Care', quantity: 2, estimatedCost: 400, setting: 'outpatient', reason: 'Routine prenatal labs' },
      { code: '36415', codeType: 'CPT', description: 'Blood draws', phase: 'Prenatal Care', quantity: 4, estimatedCost: 80, setting: 'outpatient', reason: 'Routine blood work' },
      { code: '99213', codeType: 'CPT', description: 'Postpartum visits', phase: 'Postpartum', quantity: 2, estimatedCost: 600, setting: 'outpatient', reason: 'Recovery check-ups' },
    ],
  },
  {
    triggerCodes: ['44970'],
    triggerKeywords: ['appendectomy'],
    label: 'Appendectomy',
    suggestions: [
      { code: '0450', codeType: 'REV', description: 'Emergency room', phase: 'Emergency', quantity: 1, estimatedCost: 2500, setting: 'inpatient', reason: 'ER evaluation' },
      { code: '74177', codeType: 'CPT', description: 'CT scan abdomen/pelvis', phase: 'Emergency', quantity: 1, estimatedCost: 2200, setting: 'inpatient', reason: 'Diagnostic imaging' },
      { code: '00840', codeType: 'CPT', description: 'General anesthesia', phase: 'Surgery', quantity: 1, estimatedCost: 1800, setting: 'inpatient', reason: 'Required for surgery' },
      { code: '0360', codeType: 'REV', description: 'Operating room', phase: 'Surgery', quantity: 1, estimatedCost: 8000, setting: 'inpatient', reason: 'Operating room fees' },
      { code: '88305', codeType: 'CPT', description: 'Pathology exam', phase: 'Surgery', quantity: 1, estimatedCost: 350, setting: 'inpatient', reason: 'Tissue analysis' },
      { code: '0250', codeType: 'REV', description: 'Pharmacy - antibiotics', phase: 'Recovery', quantity: 1, estimatedCost: 1200, setting: 'inpatient', reason: 'Post-op medications' },
    ],
  },
  {
    triggerCodes: ['45378', '45380', '45385'],
    triggerKeywords: ['colonoscopy'],
    label: 'Colonoscopy',
    suggestions: [
      { code: '00810', codeType: 'CPT', description: 'Anesthesia / sedation', phase: 'Procedure', quantity: 1, estimatedCost: 800, setting: 'outpatient', reason: 'Sedation for procedure' },
      { code: '0360', codeType: 'REV', description: 'Procedure room', phase: 'Procedure', quantity: 1, estimatedCost: 1200, setting: 'outpatient', reason: 'Facility fee' },
      { code: '88305', codeType: 'CPT', description: 'Pathology (if biopsy)', phase: 'Procedure', quantity: 1, estimatedCost: 350, setting: 'outpatient', reason: 'Polyp analysis if found' },
      { code: '99213', codeType: 'CPT', description: 'Results follow-up', phase: 'Follow-Up', quantity: 1, estimatedCost: 200, setting: 'outpatient', reason: 'Discuss results' },
    ],
  },
  {
    triggerCodes: ['47135'],
    triggerKeywords: ['liver transplant'],
    label: 'Liver Transplant',
    suggestions: [
      { code: '00796', codeType: 'CPT', description: 'Anesthesia for transplant', phase: 'Surgery', quantity: 1, estimatedCost: 15000, setting: 'inpatient', reason: 'Required for transplant' },
      { code: '0360', codeType: 'REV', description: 'Operating room', phase: 'Surgery', quantity: 1, estimatedCost: 85000, setting: 'inpatient', reason: 'Extended OR time' },
      { code: '99291', codeType: 'CPT', description: 'ICU critical care', phase: 'Post-Op ICU', quantity: 20, estimatedCost: 50000, setting: 'inpatient', reason: '~2 weeks ICU stay' },
      { code: '0250', codeType: 'REV', description: 'Immunosuppressant drugs', phase: 'Post-Op ICU', quantity: 1, estimatedCost: 35000, setting: 'inpatient', reason: 'Anti-rejection meds' },
      { code: '36430', codeType: 'CPT', description: 'Blood transfusion', phase: 'Post-Op ICU', quantity: 8, estimatedCost: 8000, setting: 'inpatient', reason: 'Typical for transplant' },
      { code: '80076', codeType: 'CPT', description: 'Liver function labs', phase: 'Recovery', quantity: 30, estimatedCost: 3000, setting: 'outpatient', reason: 'Ongoing monitoring' },
    ],
  },
  {
    triggerCodes: ['55866'],
    triggerKeywords: ['prostatectomy'],
    label: 'Radical Prostatectomy',
    suggestions: [
      { code: '00865', codeType: 'CPT', description: 'Anesthesia for prostatectomy', phase: 'Surgery', quantity: 1, estimatedCost: 2800, setting: 'inpatient', reason: 'Required for surgery' },
      { code: '0360', codeType: 'REV', description: 'Operating room (robotic)', phase: 'Surgery', quantity: 1, estimatedCost: 8500, setting: 'inpatient', reason: 'OR with robotic system' },
      { code: '38770', codeType: 'CPT', description: 'Pelvic lymph node removal', phase: 'Surgery', quantity: 1, estimatedCost: 4200, setting: 'inpatient', reason: 'Staging/treatment' },
      { code: '88305', codeType: 'CPT', description: 'Pathology examination', phase: 'Surgery', quantity: 1, estimatedCost: 850, setting: 'inpatient', reason: 'Cancer analysis' },
      { code: '84153', codeType: 'CPT', description: 'PSA test (monitoring)', phase: 'Follow-Up', quantity: 3, estimatedCost: 225, setting: 'outpatient', reason: 'Post-surgery cancer monitoring' },
      { code: '99213', codeType: 'CPT', description: 'Follow-up visits', phase: 'Follow-Up', quantity: 4, estimatedCost: 800, setting: 'outpatient', reason: 'Recovery check-ups' },
    ],
  },
];

/**
 * Find matching bundles for a given procedure code or description.
 * Returns suggestions that aren't already in the scenario.
 */
export function findSuggestions(
  code: string,
  description: string,
  existingCodes: Set<string>
): BundleSuggestion[] {
  const descLower = description.toLowerCase();
  const suggestions: BundleSuggestion[] = [];

  for (const bundle of PROCEDURE_BUNDLES) {
    const codeMatch = bundle.triggerCodes.includes(code);
    const keywordMatch = bundle.triggerKeywords.some(kw => descLower.includes(kw));

    if (codeMatch || keywordMatch) {
      for (const s of bundle.suggestions) {
        if (!existingCodes.has(s.code)) {
          suggestions.push(s);
        }
      }
    }
  }

  // Deduplicate by code
  const seen = new Set<string>();
  return suggestions.filter(s => {
    if (seen.has(s.code)) return false;
    seen.add(s.code);
    return true;
  });
}
